import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Books, FileText, ShieldCheck, Users, 
  Briefcase, Package, Database, ChartBar, Brain,
  MagnifyingGlass, Plus, CaretRight, CaretLeft, CaretDown,
  CheckCircle, Clock, Warning, ArrowRight, ArrowLeft,
  Eye, PencilSimple, Trash, Download, Upload,
  TreeStructure, GitBranch, Sparkle, Robot,
  ListChecks, Gauge, Target, Lightbulb, Info,
  ArrowsClockwise, Funnel, X, Check, FolderOpen,
  Coins, Gavel, UsersFour, Lock, Bank, Handshake, Leaf, Buildings,
  FilePdf, Folder
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Icon mapping for dynamic themes
const ICON_MAP = {
  Coins, Gavel, Brain, Bank, UsersFour, Handshake, ShieldCheck, Lock, Globe,
  Database, Leaf, Buildings, Folder, FileText, Briefcase, Package
};

// Default Regulatory Themes Configuration
const DEFAULT_THEMES = [
  { id: 'aml_cft_pf', name: 'AML/CFT/PF', icon: 'Coins', color: 'amber', dbKey: 'AML_CFT_PF', description: 'Anti-Money Laundering, Counter Financing of Terrorism, Proliferation Financing' },
  { id: 'anti_bribery', name: 'Anti Bribery', icon: 'Gavel', color: 'red', dbKey: 'Anti_Bribery', description: 'Anti-Bribery and Corruption compliance' },
  { id: 'ai_governance', name: 'AI Governance', icon: 'Brain', color: 'purple', dbKey: 'AI_Governance', description: 'Artificial Intelligence governance and ethics' },
  { id: 'capital_liquidity', name: 'Capital & Liquidity', icon: 'Bank', color: 'cyan', dbKey: 'Capital_Liquidity', description: 'Capital adequacy and liquidity risk management' },
  { id: 'consumer_protection', name: 'Consumer Protection', icon: 'UsersFour', color: 'blue', dbKey: 'Consumer_Protection', description: 'Consumer rights and protection regulations' },
  { id: 'conduct', name: 'Conduct', icon: 'Handshake', color: 'teal', dbKey: 'Conduct', description: 'Market conduct and fair treatment of customers' },
  { id: 'cybersecurity', name: 'Cybersecurity', icon: 'Lock', color: 'sky', dbKey: 'Cybersecurity', description: 'Cybersecurity and data protection' },
  { id: 'fatca_crs', name: 'FATCA/CRS', icon: 'Globe', color: 'indigo', dbKey: 'FATCA_CRS', description: 'Foreign Account Tax Compliance Act and Common Reporting Standard' },
  { id: 'info_privacy_security', name: 'Info Privacy & Security', icon: 'ShieldCheck', color: 'pink', dbKey: 'Info_Privacy_Security', description: 'Data protection, privacy, and information security' },
  { id: 'it_governance', name: 'IT Governance', icon: 'Database', color: 'emerald', dbKey: 'IT_Governance', description: 'Information Technology governance and risk management' },
  { id: 'social_impact', name: 'Social Impact', icon: 'Leaf', color: 'lime', dbKey: 'Social_Impact', description: 'Environmental, Social and Governance (ESG) requirements' },
  { id: 'governance', name: 'Governance', icon: 'Buildings', color: 'gold', dbKey: 'Governance', description: 'Corporate governance and board oversight' }
];

// Color mapping for themes
const COLOR_MAP = {
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', activeBorder: 'border-amber-500', gradient: 'from-amber-500/20' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', activeBorder: 'border-red-500', gradient: 'from-red-500/20' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', activeBorder: 'border-purple-500', gradient: 'from-purple-500/20' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', activeBorder: 'border-blue-500', gradient: 'from-blue-500/20' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', activeBorder: 'border-teal-500', gradient: 'from-teal-500/20' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', activeBorder: 'border-cyan-500', gradient: 'from-cyan-500/20' },
  indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', activeBorder: 'border-indigo-500', gradient: 'from-indigo-500/20' },
  sky: { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-400', activeBorder: 'border-sky-500', gradient: 'from-sky-500/20' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', activeBorder: 'border-emerald-500', gradient: 'from-emerald-500/20' },
  lime: { bg: 'bg-lime-500/10', border: 'border-lime-500/30', text: 'text-lime-400', activeBorder: 'border-lime-500', gradient: 'from-lime-500/20' },
  pink: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-400', activeBorder: 'border-pink-500', gradient: 'from-pink-500/20' },
  gold: { bg: 'bg-gold/10', border: 'border-gold/30', text: 'text-gold', activeBorder: 'border-gold', gradient: 'from-gold/20' }
};

// Map hex color to color name for custom themes
const hexToColorName = (hex) => {
  const colorMap = {
    '#F59E0B': 'amber', '#EF4444': 'red', '#A855F7': 'purple', '#0891B2': 'cyan',
    '#3B82F6': 'blue', '#14B8A6': 'teal', '#06B6D4': 'sky', '#6366F1': 'indigo',
    '#EC4899': 'pink', '#0EA5E9': 'emerald', '#10B981': 'lime', '#D4A843': 'gold',
    '#F97316': 'amber', '#8B5CF6': 'purple', '#059669': 'emerald', '#DC2626': 'red',
    '#7C3AED': 'purple', '#2563EB': 'blue'
  };
  return colorMap[hex] || 'gold';
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Draft' },
    ai_extracted: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'AI Extracted' },
    under_review: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Under Review' },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Approved' },
    active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Active' },
    superseded: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Superseded' }
  };
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'gold', onClick }) => (
  <motion.div
    whileHover={{ y: -2 }}
    onClick={onClick}
    className={`p-4 bg-surface border border-border rounded-lg ${onClick ? 'cursor-pointer hover:border-gold/30' : ''}`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon size={22} className={`text-${color}`} />
      </div>
      <span className="text-2xl font-mono font-bold text-text-primary">{value}</span>
    </div>
    <p className="text-sm text-text-secondary">{label}</p>
    {subValue && <p className="text-xs text-text-tertiary mt-1">{subValue}</p>}
  </motion.div>
);

// Theme Detail Panel Component
const ThemeDetailPanel = ({ theme, onClose, stats, getIconComponent }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [laws, setLaws] = useState([]);
  const [obligationStats, setObligationStats] = useState({ total: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const colors = COLOR_MAP[theme.color] || COLOR_MAP.gold;
  const ThemeIcon = getIconComponent ? getIconComponent(theme.icon) : (ICON_MAP[theme.icon] || Folder);

  useEffect(() => {
    fetchThemeData();
  }, [theme.id]);

  const fetchThemeData = async () => {
    setLoading(true);
    try {
      // Fetch laws for this theme
      const lawsRes = await axios.get(`${API}/regulatory-universe/laws?regulatory_theme=${theme.id}`);
      setLaws(lawsRes.data.laws || []);
      
      // Fetch obligation stats for this theme
      try {
        const obligationsRes = await axios.get(`${API}/regulatory-universe/themes/${theme.id}/obligations-grouped`);
        const stats = obligationsRes.data.stats || {};
        setObligationStats({
          total: stats.total_obligations || 0,
          pending: stats.pending_review || 0
        });
      } catch {
        setObligationStats({ total: 0, pending: 0 });
      }
    } catch (error) {
      console.error('Failed to load theme data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'laws', label: 'Laws', icon: Books, count: laws.length },
    { id: 'obligations', label: 'Obligations', icon: FileText, count: obligationStats.total, pendingCount: obligationStats.pending },
    { id: 'controls', label: 'Controls', icon: ShieldCheck, count: 0 },
    { id: 'evidence', label: 'Evidence', icon: Database, count: 0 },
    { id: 'explorer', label: 'Explorer', icon: TreeStructure }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      className="fixed inset-y-0 right-0 w-[70%] bg-background border-l border-border shadow-2xl z-40 flex flex-col"
    >
      {/* Header */}
      <div 
        className={`p-6 border-b border-border bg-gradient-to-r ${colors.gradient} to-transparent`}
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-all"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">Back to Themes</span>
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
            <ThemeIcon size={28} className={colors.text} />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-semibold text-text-primary">{theme.name}</h2>
            <p className="text-sm text-text-secondary mt-1">{theme.description}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-6">
          {tabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-all ${
                  isActive
                    ? `bg-surface ${colors.text} border-b-2 ${colors.activeBorder}`
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface/50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon size={16} />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`px-1.5 py-0.5 text-xs rounded ${isActive ? colors.bg : 'bg-background'}`}>
                    {tab.count}
                  </span>
                )}
                {tab.pendingCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400">
                    {tab.pendingCount} pending
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <ArrowsClockwise size={32} className="animate-spin text-gold" />
          </div>
        ) : activeTab === 'overview' ? (
          <ThemeOverviewTab theme={theme} laws={laws} obligationStats={obligationStats} colors={colors} />
        ) : activeTab === 'laws' ? (
          <ThemeLawsTab theme={theme} laws={laws} colors={colors} onRefresh={fetchThemeData} />
        ) : activeTab === 'obligations' ? (
          <ThemeObligationsTab theme={theme} colors={colors} />
        ) : activeTab === 'controls' ? (
          <div className="text-center py-20">
            <ShieldCheck size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
            <p className="text-text-secondary">No controls mapped yet</p>
            <p className="text-sm text-text-tertiary mt-2">Controls will appear here after parsing obligations</p>
          </div>
        ) : activeTab === 'evidence' ? (
          <div className="text-center py-20">
            <Database size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
            <p className="text-text-secondary">No evidence attached yet</p>
            <p className="text-sm text-text-tertiary mt-2">Evidence will be linked to controls</p>
          </div>
        ) : activeTab === 'explorer' ? (
          <div className="text-center py-20">
            <TreeStructure size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
            <p className="text-text-secondary">Relationship Explorer</p>
            <p className="text-sm text-text-tertiary mt-2">Visualize connections between laws, obligations, and controls</p>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
};

// Theme Overview Tab
const ThemeOverviewTab = ({ theme, laws, obligationStats, colors }) => {
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className={`p-4 ${colors.bg} border ${colors.border} rounded-lg`}>
          <div className="flex items-center gap-3 mb-2">
            <Books size={20} className={colors.text} />
            <span className="text-2xl font-mono font-bold text-text-primary">{laws.length}</span>
          </div>
          <p className="text-sm text-text-secondary">Laws</p>
        </div>
        <div className="p-4 bg-surface border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-blue-400" />
            <span className="text-2xl font-mono font-bold text-text-primary">{obligationStats.total}</span>
          </div>
          <p className="text-sm text-text-secondary">Obligations</p>
          {obligationStats.pending > 0 && (
            <p className="text-xs text-purple-400 mt-1">{obligationStats.pending} pending review</p>
          )}
        </div>
        <div className="p-4 bg-surface border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck size={20} className="text-emerald-400" />
            <span className="text-2xl font-mono font-bold text-text-primary">0</span>
          </div>
          <p className="text-sm text-text-secondary">Controls</p>
        </div>
        <div className="p-4 bg-surface border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Database size={20} className="text-purple-400" />
            <span className="text-2xl font-mono font-bold text-text-primary">0</span>
          </div>
          <p className="text-sm text-text-secondary">Evidence</p>
        </div>
      </div>

      {/* Recent Laws */}
      {laws.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Books size={18} className={colors.text} />
            Recent Laws
          </h3>
          <div className="space-y-3">
            {laws.slice(0, 5).map(law => (
              <div key={law.id} className="p-3 bg-background border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-text-primary">{law.name}</h4>
                    <p className="text-xs text-text-tertiary mt-1">
                      {law.jurisdiction} • {law.document_type || 'Document'} • {law.obligation_count || 0} obligations
                    </p>
                  </div>
                  <StatusBadge status={law.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Parsing Status Badge Component
const ParsingStatusBadge = ({ status }) => {
  const statusConfig = {
    not_parsed: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Not Parsed' },
    parsing: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Parsing...' },
    completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Parsed' },
    failed: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Failed' }
  };
  
  const config = statusConfig[status] || statusConfig.not_parsed;
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {status === 'parsing' && <ArrowsClockwise size={10} className="inline mr-1 animate-spin" />}
      {config.label}
    </span>
  );
};

// Theme Laws Tab
const ThemeLawsTab = ({ theme, laws, colors, onRefresh }) => {
  const navigate = useNavigate();
  const [parsingLawId, setParsingLawId] = useState(null);
  const [parsingStatus, setParsingStatus] = useState({});
  
  const handleParse = async (lawId) => {
    setParsingLawId(lawId);
    try {
      const response = await axios.post(`${API}/regulatory-universe/laws/${lawId}/parse`);
      if (response.data.status === 'parsing') {
        toast.success('Document parsing started');
        // Start polling for status
        pollParsingStatus(lawId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start parsing');
      setParsingLawId(null);
    }
  };
  
  const pollParsingStatus = async (lawId) => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/regulatory-universe/laws/${lawId}/parsing-status`);
        const status = response.data.parsing_status;
        
        setParsingStatus(prev => ({
          ...prev,
          [lawId]: response.data
        }));
        
        if (status === 'parsing') {
          // Continue polling
          setTimeout(checkStatus, 3000);
        } else if (status === 'completed') {
          toast.success(`Parsing completed: ${response.data.section_count} sections, ${response.data.obligation_count} obligations extracted`);
          setParsingLawId(null);
          onRefresh();
        } else if (status === 'failed') {
          toast.error(`Parsing failed: ${response.data.parsing_error || 'Unknown error'}`);
          setParsingLawId(null);
        }
      } catch (error) {
        console.error('Error polling parsing status:', error);
        setParsingLawId(null);
      }
    };
    
    checkStatus();
  };
  
  const handleRetryParsing = async (lawId) => {
    setParsingLawId(lawId);
    try {
      const response = await axios.post(`${API}/regulatory-universe/laws/${lawId}/retry-parsing`);
      if (response.data.status === 'parsing') {
        toast.success('Retrying document parsing');
        pollParsingStatus(lawId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry parsing');
      setParsingLawId(null);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Laws & Legislation</h3>
          <p className="text-sm text-text-secondary">Documents published to {theme.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:border-gold hover:text-gold transition-all flex items-center gap-2"
          >
            <ArrowsClockwise size={14} />
            Refresh
          </button>
        </div>
      </div>

      {laws.length > 0 ? (
        <div className="space-y-3">
          {laws.map(law => {
            const lawParsingStatus = parsingStatus[law.id]?.parsing_status || law.parsing_status || 'not_parsed';
            const isCurrentlyParsing = parsingLawId === law.id || lawParsingStatus === 'parsing';
            const sectionCount = parsingStatus[law.id]?.section_count || 0;
            const obligationCount = parsingStatus[law.id]?.obligation_count || 0;
            
            return (
              <div key={law.id} className="bg-surface border border-border rounded-lg p-4 hover:border-gold/30 transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FilePdf size={22} className="text-red-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{law.name}</h4>
                      {law.short_name && (
                        <span className="text-xs text-text-tertiary">({law.short_name})</span>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="px-2 py-0.5 text-xs rounded bg-background text-text-tertiary">
                          {law.document_type || 'Document'}
                        </span>
                        <span className="text-xs text-text-tertiary">
                          {law.jurisdiction}
                        </span>
                        {law.regulator && (
                          <span className="text-xs text-text-tertiary">
                            • {law.regulator}
                          </span>
                        )}
                      </div>
                      {law.description && (
                        <p className="text-sm text-text-secondary mt-2 line-clamp-2">{law.description}</p>
                      )}
                      
                      {/* Parsing Results Summary */}
                      {lawParsingStatus === 'completed' && (sectionCount > 0 || obligationCount > 0) && (
                        <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                          <span className="flex items-center gap-1">
                            <ListChecks size={12} className="text-emerald-400" />
                            {sectionCount} sections
                          </span>
                          <span className="flex items-center gap-1">
                            <Target size={12} className="text-purple-400" />
                            {obligationCount} obligations
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={law.status} />
                    <ParsingStatusBadge status={lawParsingStatus} />
                    
                    {/* Parse / Retry Button */}
                    {lawParsingStatus === 'not_parsed' && (
                      <button 
                        onClick={() => handleParse(law.id)}
                        disabled={isCurrentlyParsing}
                        className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`parse-law-${law.id}`}
                      >
                        {isCurrentlyParsing ? (
                          <ArrowsClockwise size={14} className="animate-spin" />
                        ) : (
                          <Brain size={14} />
                        )}
                        {isCurrentlyParsing ? 'Parsing...' : 'Parse Document'}
                      </button>
                    )}
                    
                    {lawParsingStatus === 'parsing' && (
                      <button 
                        disabled
                        className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-xs font-medium flex items-center gap-1.5"
                      >
                        <ArrowsClockwise size={14} className="animate-spin" />
                        Parsing...
                      </button>
                    )}
                    
                    {lawParsingStatus === 'failed' && (
                      <button 
                        onClick={() => handleRetryParsing(law.id)}
                        disabled={isCurrentlyParsing}
                        className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <ArrowsClockwise size={14} />
                        Retry
                      </button>
                    )}
                    
                    {lawParsingStatus === 'completed' && (
                      <button 
                        onClick={() => handleParse(law.id)}
                        disabled={isCurrentlyParsing}
                        className="px-3 py-1.5 bg-surface border border-border text-text-secondary rounded-lg text-xs font-medium hover:border-gold hover:text-gold transition-all flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <ArrowsClockwise size={14} />
                        Re-parse
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Parsing Error */}
                {lawParsingStatus === 'failed' && parsingStatus[law.id]?.parsing_error && (
                  <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                    <Warning size={12} className="inline mr-1" />
                    {parsingStatus[law.id].parsing_error}
                  </div>
                )}
                
                {/* Source Documents */}
                {law.source_documents && law.source_documents.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-text-tertiary mb-2">Source Documents ({law.source_documents.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {law.source_documents.map((doc, idx) => (
                        <span key={idx} className="px-2 py-1 bg-background text-xs text-text-secondary rounded flex items-center gap-1">
                          <FilePdf size={12} className="text-red-400" />
                          {doc.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-surface border border-border rounded-lg">
          <Books size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
          <p className="text-text-secondary mb-2">No laws in this theme yet</p>
          <p className="text-sm text-text-tertiary">
            Documents published from the <Link to="/library" className="text-gold hover:underline">Regulatory Library</Link> will appear here
          </p>
        </div>
      )}
    </div>
  );
};

// Review Status Badge for Obligations
const ReviewStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Draft' },
    ai_extracted: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'AI Extracted' },
    under_review: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Under Review' },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Approved' },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Rejected' }
  };
  const config = statusConfig[status] || statusConfig.ai_extracted;
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Priority Badge
const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    critical: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Critical' },
    high: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'High' },
    medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Medium' },
    low: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Low' }
  };
  const config = priorityConfig[priority] || priorityConfig.medium;
  return (
    <span className={`px-2 py-0.5 text-xs rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Individual Obligation Card with Approve/Reject and Selection
const ObligationCard = ({ obligation, onApprove, onReject, isProcessing, isSelected, onSelect, showCheckbox }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const confidencePercent = obligation.confidence_score 
    ? Math.round(obligation.confidence_score * 100) 
    : null;
  
  const isReviewed = ['approved', 'rejected'].includes(obligation.review_status);
  const isPending = ['ai_extracted', 'under_review'].includes(obligation.review_status);
  
  return (
    <div className={`bg-background border rounded-lg p-4 transition-all ${
      isReviewed ? 'border-border opacity-80' : isSelected ? 'border-gold ring-1 ring-gold/30' : 'border-gold/30 hover:border-gold/50'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Checkbox for bulk selection */}
          {showCheckbox && isPending && (
            <label className="flex items-center cursor-pointer mt-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(obligation.id)}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold focus:ring-offset-0 cursor-pointer"
              />
            </label>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <ReviewStatusBadge status={obligation.review_status} />
              <PriorityBadge priority={obligation.priority} />
              {confidencePercent && (
                <span className="text-xs text-text-tertiary flex items-center gap-1">
                  <Gauge size={12} />
                  {confidencePercent}% confidence
                </span>
              )}
              {obligation.is_reporting_obligation && (
                <span className="px-2 py-0.5 text-xs rounded bg-cyan-500/10 text-cyan-400">
                  Reporting
                </span>
              )}
            </div>
            
            {/* Statement */}
            <p className="text-sm text-text-primary mb-2">
              {obligation.statement_summary || obligation.statement}
            </p>
            
            {/* Source citation */}
            {obligation.section_reference && (
              <p className="text-xs text-gold flex items-center gap-1">
                <ListChecks size={12} />
                {obligation.section_reference}
                {obligation.section_title && ` - ${obligation.section_title}`}
              </p>
            )}
          </div>
        </div>
        
        {/* Actions */}
        {isPending && !showCheckbox && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onApprove(obligation.id)}
              disabled={isProcessing}
              className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`approve-obligation-${obligation.id}`}
            >
              <Check size={14} />
              Approve
            </button>
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={isProcessing}
              className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`reject-obligation-${obligation.id}`}
            >
              <X size={14} />
              Reject
            </button>
          </div>
        )}
        
        {isReviewed && (
          <div className="text-xs text-text-tertiary">
            {obligation.reviewed_by && (
              <p>by {obligation.reviewed_by}</p>
            )}
          </div>
        )}
      </div>
      
      {/* Expandable Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-3 text-xs text-text-secondary hover:text-gold flex items-center gap-1"
      >
        <CaretDown size={12} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        {showDetails ? 'Hide details' : 'Show details'}
      </button>
      
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs">
          {obligation.regulated_party && (
            <div>
              <span className="text-text-tertiary">Regulated Party: </span>
              <span className="text-text-secondary">{obligation.regulated_party}</span>
            </div>
          )}
          {obligation.action_required && (
            <div>
              <span className="text-text-tertiary">Action Required: </span>
              <span className="text-text-secondary">{obligation.action_required}</span>
            </div>
          )}
          {obligation.timeframe && (
            <div>
              <span className="text-text-tertiary">Timeframe: </span>
              <span className="text-text-secondary">{obligation.timeframe}</span>
            </div>
          )}
          {obligation.penalties && (
            <div>
              <span className="text-text-tertiary">Penalties: </span>
              <span className="text-text-secondary">{obligation.penalties}</span>
            </div>
          )}
          {obligation.source_citation && (
            <div>
              <span className="text-text-tertiary">Source: </span>
              <span className="text-text-secondary">{obligation.source_citation}</span>
            </div>
          )}
          {obligation.review_notes && (
            <div className="mt-2 p-2 bg-surface rounded">
              <span className="text-text-tertiary">Review Notes: </span>
              <span className="text-text-secondary">{obligation.review_notes}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRejectModal(false)}>
          <div className="bg-surface border border-border rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Reject Obligation</h3>
            <p className="text-sm text-text-secondary mb-4">
              Please provide a reason for rejecting this obligation:
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:border-gold focus:outline-none resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onReject(obligation.id, rejectReason);
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-all disabled:opacity-50"
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Law Section with Expandable Obligations and Bulk Selection
const LawObligationsSection = ({ law, onApprove, onReject, isProcessing, defaultExpanded = false, selectedIds, onSelectObligation, onSelectAllInLaw, bulkMode }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  const pendingObligations = law.obligations.filter(o => 
    ['ai_extracted', 'under_review'].includes(o.review_status)
  );
  const pendingCount = pendingObligations.length;
  const approvedCount = law.obligations.filter(o => o.review_status === 'approved').length;
  const rejectedCount = law.obligations.filter(o => o.review_status === 'rejected').length;
  
  // Check if all pending in this law are selected
  const allPendingSelected = pendingObligations.length > 0 && 
    pendingObligations.every(o => selectedIds.includes(o.id));
  const somePendingSelected = pendingObligations.some(o => selectedIds.includes(o.id));
  
  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Law Header - Clickable to expand */}
      <div className="flex items-center">
        {/* Select all checkbox for this law */}
        {bulkMode && pendingCount > 0 && (
          <div className="pl-4 pr-2 py-4 border-r border-border">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allPendingSelected}
                ref={el => {
                  if (el) el.indeterminate = somePendingSelected && !allPendingSelected;
                }}
                onChange={() => onSelectAllInLaw(law.law_id, pendingObligations.map(o => o.id))}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold focus:ring-offset-0 cursor-pointer"
              />
            </label>
          </div>
        )}
        
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 p-4 flex items-center justify-between hover:bg-background/50 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FilePdf size={20} className="text-red-400" />
            </div>
            <div className="text-left">
              <h4 className="font-medium text-text-primary">{law.law_name}</h4>
              <p className="text-xs text-text-tertiary">
                {law.jurisdiction} • {law.obligation_count} obligations
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status counts */}
            <div className="flex items-center gap-2 text-xs">
              {pendingCount > 0 && (
                <span className="px-2 py-1 rounded bg-purple-500/10 text-purple-400">
                  {pendingCount} pending
                </span>
              )}
              {approvedCount > 0 && (
                <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400">
                  {approvedCount} approved
                </span>
              )}
              {rejectedCount > 0 && (
                <span className="px-2 py-1 rounded bg-red-500/10 text-red-400">
                  {rejectedCount} rejected
                </span>
              )}
            </div>
            
            <CaretDown 
              size={20} 
              className={`text-text-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`} 
            />
          </div>
        </button>
      </div>
      
      {/* Obligations List */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-3">
          {law.obligations.length > 0 ? (
            law.obligations.map(ob => (
              <ObligationCard
                key={ob.id}
                obligation={ob}
                onApprove={onApprove}
                onReject={onReject}
                isProcessing={isProcessing}
                isSelected={selectedIds.includes(ob.id)}
                onSelect={onSelectObligation}
                showCheckbox={bulkMode}
              />
            ))
          ) : (
            <p className="text-sm text-text-tertiary text-center py-4">
              No obligations extracted from this document yet
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Theme Obligations Tab - Complete Review Queue UI with Bulk Actions
const ThemeObligationsTab = ({ theme, colors }) => {
  const [loading, setLoading] = useState(true);
  const [lawsWithObligations, setLawsWithObligations] = useState([]);
  const [stats, setStats] = useState({ total_obligations: 0, pending_review: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [processingId, setProcessingId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [bulkApproveNotes, setBulkApproveNotes] = useState('');
  
  useEffect(() => {
    fetchObligations();
  }, [theme.id, filter]);
  
  // Clear selections when switching themes or filters
  useEffect(() => {
    setSelectedIds([]);
  }, [theme.id, filter]);
  
  const fetchObligations = async () => {
    setLoading(true);
    try {
      const statusParam = filter !== 'all' ? `&status=${filter === 'pending' ? 'ai_extracted' : filter}` : '';
      const response = await axios.get(`${API}/regulatory-universe/themes/${theme.id}/obligations-grouped?${statusParam}`);
      setLawsWithObligations(response.data.laws || []);
      setStats(response.data.stats || { total_obligations: 0, pending_review: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to load obligations:', error);
      toast.error('Failed to load obligations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async (obligationId) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/regulatory-universe/obligations/${obligationId}/approve`);
      toast.success('Obligation approved');
      fetchObligations(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve obligation');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleReject = async (obligationId, reason) => {
    setProcessingId(obligationId);
    try {
      await axios.post(`${API}/regulatory-universe/obligations/${obligationId}/reject`, null, {
        params: { review_notes: reason }
      });
      toast.success('Obligation rejected');
      fetchObligations(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject obligation');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleSelectObligation = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  
  const handleSelectAllInLaw = (lawId, obligationIds) => {
    const allSelected = obligationIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      // Deselect all from this law
      setSelectedIds(prev => prev.filter(id => !obligationIds.includes(id)));
    } else {
      // Select all from this law
      setSelectedIds(prev => [...new Set([...prev, ...obligationIds])]);
    }
  };
  
  const handleSelectAll = () => {
    const allPendingIds = lawsWithObligations.flatMap(law => 
      law.obligations.filter(o => ['ai_extracted', 'under_review'].includes(o.review_status)).map(o => o.id)
    );
    
    const allSelected = allPendingIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allPendingIds);
    }
  };
  
  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkProcessing(true);
    try {
      const response = await axios.post(`${API}/regulatory-universe/obligations/bulk-approve`, {
        obligation_ids: selectedIds,
        review_notes: bulkApproveNotes || 'Bulk approved'
      });
      
      toast.success(`Approved ${response.data.success_count} obligations`);
      if (response.data.failed_count > 0) {
        toast.error(`Failed to approve ${response.data.failed_count} obligations`);
      }
      
      setSelectedIds([]);
      setBulkApproveNotes('');
      setShowBulkApproveModal(false);
      setBulkMode(false);
      fetchObligations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve obligations');
    } finally {
      setBulkProcessing(false);
    }
  };
  
  const filterTabs = [
    { id: 'all', label: 'All', count: stats.total_obligations },
    { id: 'pending', label: 'Pending Review', count: stats.pending_review },
    { id: 'approved', label: 'Approved', count: stats.approved },
    { id: 'rejected', label: 'Rejected', count: stats.rejected }
  ];
  
  // Count all pending obligations
  const allPendingIds = lawsWithObligations.flatMap(law => 
    law.obligations.filter(o => ['ai_extracted', 'under_review'].includes(o.review_status)).map(o => o.id)
  );
  const allPendingSelected = allPendingIds.length > 0 && allPendingIds.every(id => selectedIds.includes(id));
  
  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Obligations Review Queue</h3>
          <p className="text-sm text-text-secondary">
            Review and approve AI-extracted obligations from {theme.name} laws
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stats.pending_review > 0 && (
            <button
              onClick={() => {
                setBulkMode(!bulkMode);
                if (bulkMode) setSelectedIds([]);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                bulkMode 
                  ? 'bg-gold/10 text-gold border border-gold' 
                  : 'bg-surface border border-border text-text-secondary hover:border-gold hover:text-gold'
              }`}
              data-testid="bulk-mode-toggle"
            >
              <ListChecks size={14} />
              {bulkMode ? 'Exit Bulk Mode' : 'Bulk Select'}
            </button>
          )}
          <button
            onClick={fetchObligations}
            className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:border-gold hover:text-gold transition-all flex items-center gap-2"
          >
            <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Bulk Action Bar - Fixed at top when in bulk mode with selections */}
      {bulkMode && selectedIds.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-20 bg-gold/10 border border-gold/30 rounded-lg p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
              />
              <span className="text-sm text-gold font-medium">
                {selectedIds.length} selected
              </span>
            </label>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-text-secondary hover:text-gold"
            >
              Clear selection
            </button>
          </div>
          <button
            onClick={() => setShowBulkApproveModal(true)}
            disabled={bulkProcessing}
            className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/30 transition-all flex items-center gap-2 disabled:opacity-50"
            data-testid="bulk-approve-button"
          >
            <Check size={16} />
            Approve {selectedIds.length} Selected
          </button>
        </motion.div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 bg-surface border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={18} className="text-blue-400" />
            <span className="text-2xl font-mono font-bold text-text-primary">{stats.total_obligations}</span>
          </div>
          <p className="text-xs text-text-tertiary">Total Obligations</p>
        </div>
        <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={18} className="text-purple-400" />
            <span className="text-2xl font-mono font-bold text-purple-400">{stats.pending_review}</span>
          </div>
          <p className="text-xs text-text-tertiary">Pending Review</p>
        </div>
        <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={18} className="text-emerald-400" />
            <span className="text-2xl font-mono font-bold text-emerald-400">{stats.approved}</span>
          </div>
          <p className="text-xs text-text-tertiary">Approved</p>
        </div>
        <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <X size={18} className="text-red-400" />
            <span className="text-2xl font-mono font-bold text-red-400">{stats.rejected}</span>
          </div>
          <p className="text-xs text-text-tertiary">Rejected</p>
        </div>
      </div>
      
      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-2">
        {filterTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all flex items-center gap-2 ${
              filter === tab.id
                ? 'bg-gold/10 text-gold border-b-2 border-gold -mb-[2px]'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface/50'
            }`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 text-xs rounded ${
              filter === tab.id ? 'bg-gold/20' : 'bg-background'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>
      
      {/* Laws with Obligations */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <ArrowsClockwise size={32} className="animate-spin text-gold" />
        </div>
      ) : lawsWithObligations.length > 0 ? (
        <div className="space-y-4">
          {lawsWithObligations.map((law, idx) => (
            <LawObligationsSection
              key={law.law_id}
              law={law}
              onApprove={handleApprove}
              onReject={handleReject}
              isProcessing={processingId !== null || bulkProcessing}
              defaultExpanded={idx === 0 && law.obligations.length > 0}
              selectedIds={selectedIds}
              onSelectObligation={handleSelectObligation}
              onSelectAllInLaw={handleSelectAllInLaw}
              bulkMode={bulkMode}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-surface border border-border rounded-lg">
          <FileText size={48} className="mx-auto text-text-tertiary mb-4 opacity-50" />
          <p className="text-text-secondary mb-2">
            {filter === 'all' ? 'No obligations extracted yet' : `No ${filter} obligations`}
          </p>
          <p className="text-sm text-text-tertiary max-w-md mx-auto">
            {filter === 'all' 
              ? 'Upload legislation to the Master Library, publish to the Regulatory Universe, then use NAVI to parse and extract obligations.'
              : 'Try changing the filter to view other obligations.'}
          </p>
        </div>
      )}
      
      {/* Bulk Approve Modal */}
      {showBulkApproveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkApproveModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-border rounded-xl p-6 max-w-md w-full" 
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-text-primary mb-2">Bulk Approve Obligations</h3>
            <p className="text-sm text-text-secondary mb-4">
              You are about to approve <span className="text-gold font-semibold">{selectedIds.length}</span> obligations. 
              This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="text-sm text-text-secondary mb-2 block">Review Notes (optional)</label>
              <textarea
                value={bulkApproveNotes}
                onChange={e => setBulkApproveNotes(e.target.value)}
                placeholder="Add notes for all approved obligations..."
                className="w-full p-3 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:border-gold focus:outline-none resize-none"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkApproveModal(false);
                  setBulkApproveNotes('');
                }}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkApprove}
                disabled={bulkProcessing}
                className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-bulk-approve"
              >
                {bulkProcessing ? (
                  <>
                    <ArrowsClockwise size={14} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Approve {selectedIds.length} Obligations
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Main Regulatory Universe Dashboard
const RegulatoryUniversePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeLawCounts, setThemeLawCounts] = useState({});
  const [themes, setThemes] = useState(DEFAULT_THEMES);

  // Get icon component from string name
  const getIconComponent = (iconName) => ICON_MAP[iconName] || Folder;

  useEffect(() => {
    fetchThemes();
    fetchData();
  }, []);

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API}/regulatory-themes`);
      if (response.data.themes) {
        // Convert API themes to match expected format
        const convertedThemes = response.data.themes.map(t => ({
          id: t.id,
          name: t.label,
          icon: t.icon || 'Folder',
          color: t.color ? hexToColorName(t.color) : 'gold',
          dbKey: t.dbKey,
          description: t.description,
          isCustom: t.isCustom
        }));
        setThemes(convertedThemes);
      }
    } catch {
      // Fallback to default themes
      setThemes(DEFAULT_THEMES);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await axios.get(`${API}/regulatory-universe/stats`);
      setStats(statsRes.data);
      
      // Fetch law counts for each theme
      const lawsRes = await axios.get(`${API}/regulatory-universe/laws?page_size=100`);
      const laws = lawsRes.data.laws || [];
      
      // Count laws by theme
      const counts = {};
      themes.forEach(theme => {
        counts[theme.id] = laws.filter(law => law.regulatory_theme === theme.id).length;
      });
      setThemeLawCounts(counts);
      
    } catch (error) {
      console.error('Failed to load Regulatory Universe data:', error);
      toast.error('Failed to load Regulatory Universe data');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeClick = (theme) => {
    setSelectedTheme(theme);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="ml-64 flex-1 overflow-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Globe size={24} className="text-gold" />
                </div>
                <div>
                  <h1 className="text-xl font-heading font-semibold text-text-primary">
                    Regulatory Universe
                  </h1>
                  <p className="text-xs text-text-tertiary">
                    Machine-readable regulatory intelligence
                  </p>
                </div>
              </div>
              <Link 
                to="/regulatory-universe/risk-dashboard"
                className="px-4 py-2 bg-gold/10 text-gold border border-gold/30 rounded-lg text-sm font-medium hover:bg-gold/20 transition-colors flex items-center gap-2"
              >
                <ChartBar size={16} />
                Risk Assessment Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="h-24 bg-surface rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4">
                <StatCard
                  icon={Books}
                  label="Laws"
                  value={stats?.total_laws || 0}
                  color="gold"
                />
                <StatCard
                  icon={FileText}
                  label="Obligations"
                  value={stats?.total_obligations || 0}
                  subValue={`${stats?.pending_reviews || 0} pending review`}
                  color="blue-400"
                />
                <StatCard
                  icon={ShieldCheck}
                  label="Controls"
                  value={stats?.total_controls || 0}
                  color="emerald-400"
                />
                <StatCard
                  icon={Database}
                  label="Evidence"
                  value={stats?.total_evidence || 0}
                  color="purple-400"
                />
              </div>

              {/* Regulatory Themes Grid */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Funnel size={18} className="text-gold" />
                  <h3 className="font-semibold text-text-primary">Regulatory Themes</h3>
                  <span className="text-xs text-text-tertiary ml-2">Click to view details</span>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  {themes.map(theme => {
                    const lawCount = themeLawCounts[theme.id] || 0;
                    const colors = COLOR_MAP[theme.color] || COLOR_MAP.gold;
                    const ThemeIcon = getIconComponent(theme.icon);
                    
                    return (
                      <motion.button
                        key={theme.id}
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleThemeClick(theme)}
                        className={`p-5 rounded-xl border transition-all text-left ${colors.bg} ${colors.border} hover:${colors.activeBorder}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className={`w-10 h-10 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                            <ThemeIcon size={22} className={colors.text} />
                          </div>
                          <div className="flex items-center gap-2">
                            {theme.isCustom && (
                              <span className="text-xs px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded">Custom</span>
                            )}
                            <CaretRight size={16} className="text-text-tertiary" />
                          </div>
                        </div>
                        <h4 className={`font-semibold ${colors.text} text-sm mb-1`}>{theme.name}</h4>
                        <p className="text-2xl font-mono font-bold text-text-primary">{lawCount}</p>
                        <p className="text-xs text-text-tertiary">laws</p>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Theme Detail Panel */}
      <AnimatePresence>
        {selectedTheme && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30"
              onClick={() => setSelectedTheme(null)}
            />
            <ThemeDetailPanel 
              theme={selectedTheme} 
              onClose={() => setSelectedTheme(null)}
              stats={stats}
              getIconComponent={getIconComponent}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegulatoryUniversePage;
